import { useCallback, useEffect, useRef, useState } from 'react';

const OUTER_SIZE_PX = 100;
const KNOB_SIZE_PX = 42;
const MAX_RADIUS_PX = (OUTER_SIZE_PX - KNOB_SIZE_PX) / 2;
const DEAD_ZONE = 0.08;

/**
 * @param {{
 *   onInputChange: (input: { x: number; y: number; active: boolean }) => void;
 *   disabled?: boolean;
 *   'aria-label'?: string;
 * }} props
 */
function AnalogJoystick({ onInputChange, disabled = false, 'aria-label': ariaLabel = 'Move camera' }) {
  const baseRef = useRef(null);
  const pointerIdRef = useRef(null);
  const onInputChangeRef = useRef(onInputChange);
  const [knobOffset, setKnobOffset] = useState({ x: 0, y: 0 });
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    onInputChangeRef.current = onInputChange;
  }, [onInputChange]);

  const emitInput = useCallback((x, y, active) => {
    const magnitude = Math.hypot(x, y);
    const scaled =
      magnitude <= DEAD_ZONE
        ? { x: 0, y: 0 }
        : {
            x: x / magnitude,
            y: y / magnitude,
          };
    const strength = magnitude <= DEAD_ZONE ? 0 : Math.min(1, (magnitude - DEAD_ZONE) / (1 - DEAD_ZONE));
    onInputChangeRef.current({
      x: scaled.x * strength,
      y: scaled.y * strength,
      active,
    });
  }, []);

  const updateFromPointer = useCallback(
    (clientX, clientY) => {
      const base = baseRef.current;
      if (!base) return;
      const rect = base.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      let dx = clientX - centerX;
      let dy = clientY - centerY;
      const distance = Math.hypot(dx, dy);
      if (distance > MAX_RADIUS_PX) {
        const scale = MAX_RADIUS_PX / distance;
        dx *= scale;
        dy *= scale;
      }
      setKnobOffset({ x: dx, y: dy });
      emitInput(dx / MAX_RADIUS_PX, dy / MAX_RADIUS_PX, true);
    },
    [emitInput],
  );

  const resetJoystick = useCallback(() => {
    pointerIdRef.current = null;
    setIsActive(false);
    setKnobOffset({ x: 0, y: 0 });
    emitInput(0, 0, false);
  }, [emitInput]);

  const handlePointerDown = (event) => {
    if (disabled) return;
    event.preventDefault();
    pointerIdRef.current = event.pointerId;
    setIsActive(true);
    baseRef.current?.setPointerCapture?.(event.pointerId);
    updateFromPointer(event.clientX, event.clientY);
  };

  const handlePointerMove = (event) => {
    if (pointerIdRef.current !== event.pointerId) return;
    event.preventDefault();
    updateFromPointer(event.clientX, event.clientY);
  };

  const handlePointerUp = (event) => {
    if (pointerIdRef.current !== event.pointerId) return;
    baseRef.current?.releasePointerCapture?.(event.pointerId);
    resetJoystick();
  };

  useEffect(() => {
    if (!disabled) return undefined;
    resetJoystick();
    return undefined;
  }, [disabled, resetJoystick]);

  return (
    <div
      ref={baseRef}
      className={`floorplan-room-render-joystick${isActive ? ' floorplan-room-render-joystick--active' : ''}${
        disabled ? ' floorplan-room-render-joystick--disabled' : ''
      }`}
      role="application"
      aria-label={ariaLabel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <div
        className="floorplan-room-render-joystick-knob"
        style={{
          transform: `translate(calc(-50% + ${knobOffset.x}px), calc(-50% + ${knobOffset.y}px))`,
        }}
        aria-hidden="true"
      />
    </div>
  );
}

export default AnalogJoystick;
